import * as JSZip from "jszip";

export interface PackSource {
    loadText(name: string): Promise<string>;

    preloadImages(urls: string[]): Promise<void>;

    getImage(url: string): HTMLImageElement;
};

export class ServedPackSource implements PackSource {
    static #IMAGE_CACHE: { [url: string]: HTMLImageElement } = {};
    static #PROMISE_CACHE: { [url: string]: Promise<HTMLImageElement> } = {};

    #baseUrl: string

    constructor(baseUrl: string) {
        this.#baseUrl = baseUrl;
    }

    async loadText(name: string): Promise<string> {
        return await (await fetch(this.#baseUrl + "/" + name)).text()
    }

    #loadImage(url: string): Promise<HTMLImageElement> {
        const realUrl = this.#baseUrl + "/" + url;

        if (ServedPackSource.#PROMISE_CACHE[realUrl])
            return ServedPackSource.#PROMISE_CACHE[realUrl]

        const img = new Image();
        img.src = realUrl;
        const promise = new Promise<HTMLImageElement>(function (resolve, reject) {
            img.addEventListener('load', () => {
                ServedPackSource.#IMAGE_CACHE[realUrl] = img;
                resolve(img);
            });
        })
        ServedPackSource.#PROMISE_CACHE[realUrl] = promise;
        return promise;
    }

    async preloadImages(urls: string[]): Promise<void> {
        await Promise.all(urls.map(x => this.#loadImage(x)));
    }

    getImage(url: string): HTMLImageElement {
        return ServedPackSource.#IMAGE_CACHE[this.#baseUrl + "/" + url];
    }
}

export class ZipPackSource implements PackSource {
    #zip: JSZip
    #imageCache: { [url: string]: HTMLImageElement } = {};
    #promiseCache: { [url: string]: Promise<HTMLImageElement> } = {};

    constructor(zip: JSZip) {
        this.#zip = zip;
    }

    loadText(name: string): Promise<string> {
        const file = this.#zip.file(name);
        return file.async("string");
    }

    #loadImage(url: string): Promise<HTMLImageElement> {
        if (this.#promiseCache[url])
            return this.#promiseCache[url]

        return this.#loadImageImpl(url);
    }

    async #loadImageImpl(url: string): Promise<HTMLImageElement> {
        const img = new Image();
        const imgBlob = await this.#zip.file(url).async("blob");
        img.src = URL.createObjectURL(imgBlob);
        const hoistedThis = this;
        const promise = new Promise<HTMLImageElement>((resolve, reject) => {
            img.addEventListener('load', () => {
                hoistedThis.#imageCache[url] = img;
                URL.revokeObjectURL(img.src);
                resolve(img);
            });
        });
        this.#promiseCache[url] = promise;
        return await promise;
    }

    async preloadImages(urls: string[]): Promise<void> {
        await Promise.all(urls.map(x => this.#loadImage(x)));
    }

    getImage(url: string): HTMLImageElement {
        return this.#imageCache[url];
    }

}